import React, { useState, useEffect, useCallback } from 'react';
import { useUserData } from '../../hooks/useUserData';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Checkbox,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as FacilityIcon,
  AdminPanelSettings as AdminIcon,
  Settings as SettingsIcon,
  Description as ReportIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon
} from '@mui/icons-material';
import { getAllFacilities, getAllFacilityGroups } from '../../services/facilityService';
import { useLocale } from '../../context/LocaleContext';
import ReportConfigService from '../../services/reportConfigService';

// Styled Components - Modern Theme
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
}));

// Report type configurations
const REPORT_TYPES = {
  clinical: {
    label: 'Clinical/Medical Reports',
    icon: <ReportIcon />,
    reports: [
      { key: 'hp', name: 'History & Physical (H&P)', description: 'Summary of initial admission evaluation' },
      { key: 'progress_notes', name: 'Progress Notes', description: 'Daily documentation from providers or care team' },
      { key: 'consult_notes', name: 'Consult Notes', description: 'Specialist evaluations' },
      { key: 'discharge_summary', name: 'Discharge Summary', description: 'Required clinical summary upon discharge' },
      { key: 'mar', name: 'Medication Administration Record (MAR)', description: 'Detailed record of medication administration times, doses' },
      { key: 'flowsheets', name: 'Flowsheets', description: 'Vitals, nursing assessments, intake/output, Braden, etc.' },
      { key: 'care_plans', name: 'Care Plans', description: 'Interdisciplinary care plans, goals, interventions' },
      { key: 'lab_reports', name: 'Lab Reports', description: 'Pathology, microbiology, hematology with trends' },
      { key: 'radiology_reports', name: 'Radiology Reports', description: 'Narrative results of imaging studies' },
      { key: 'operative_reports', name: 'Operative Reports', description: 'Details from surgeries or procedures' },
      { key: 'interdisciplinary_rounding', name: 'Interdisciplinary Rounding Report', description: 'Combines key data across disciplines for rounds' },
      { key: 'allergies_problems', name: 'Allergies & Problem List Summary', description: 'Active problem list, historical diagnoses' },
      { key: 'clinical_summary', name: 'Clinical Summary (Snapshot)', description: 'Aggregated view: vitals, meds, labs, allergies, problems, orders' }
    ]
  },
  operational: {
    label: 'Operational/Administrative Reports',
    icon: <SettingsIcon />,
    reports: [
      { key: 'census_report', name: 'Census Report', description: 'List of admitted patients with bed/unit status' },
      { key: 'bed_movement', name: 'Bed Movement History', description: 'Admission, transfer, discharge history by patient' },
      { key: 'shift_handoff', name: 'Shift Change/Handoff Summary', description: 'Snapshot for incoming staff (SBAR-based)' },
      { key: 'care_team_assignment', name: 'Care Team Assignment Report', description: 'Current team members per patient/unit' },
      { key: 'restraints_log', name: 'Restraints Log', description: 'Use, justification, timing of restraints' },
      { key: 'pain_assessment', name: 'Pain Assessment Audit', description: 'Pain scores, interventions, reassessments' }
    ]
  },
  quality: {
    label: 'Quality & Compliance Reports',
    icon: <AdminIcon />,
    reports: [
      { key: 'sepsis_screening', name: 'Sepsis Screening Report', description: 'Patients meeting early warning criteria' },
      { key: 'falls_risk', name: 'Falls & Risk Assessment Report', description: 'Braden, Morse scores, interventions applied' },
      { key: 'medication_reconciliation', name: 'Medication Reconciliation Completion', description: 'Gaps at admission or discharge' },
      { key: 'timely_discharge', name: 'Timely Discharge Documentation', description: 'Timestamps of discharge orders vs. summary completion' },
      { key: 'core_measures', name: 'Core Measures Reports (CMS, TJC)', description: 'Compliance with CHF, AMI, Stroke, VTE bundles' }
    ]
  },
  billing: {
    label: 'Billing/Financial Reports',
    icon: <FacilityIcon />,
    reports: [
      { key: 'charge_capture', name: 'Charge Capture Summary', description: 'Procedures, labs, meds billed' },
      { key: 'drg_report', name: 'DRG/MS-DRG Report', description: 'Diagnoses-related grouping for reimbursement' },
      { key: 'length_of_stay', name: 'Length of Stay Report', description: 'LOS by patient, diagnosis, or DRG' },
      { key: 'eligibility_verification', name: 'Eligibility and Insurance Verification', description: 'Real-time payer status' },
      { key: 'utilization_review', name: 'Utilization Review/Case Mgmt Logs', description: 'Justification of admission level, concurrent reviews' }
    ]
  }
};

const ReportConfiguration = () => {
  const { t } = useLocale();
  const { user, isAdmin, isFacilityAdmin, defaultFacility } = useUserData();
  const [currentTab, setCurrentTab] = useState(0);
  const [configurations, setConfigurations] = useState({});
  const [facilities, setFacilities] = useState([]);
  const [facilityGroups, setFacilityGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);
  const [reportConfig, setReportConfig] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Facility tree state
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedFacilities, setSelectedFacilities] = useState([]);

  const canEdit = isAdmin || isFacilityAdmin;

  const reportTypes = Object.keys(REPORT_TYPES);
  const currentReportType = reportTypes[currentTab];

  // Get facilities available to the current user
  const getAccessibleFacilities = useCallback(() => {
    if (isAdmin) {
      return facilities;
    }
    // Facility admin can only see their assigned facilities
    if (isFacilityAdmin && defaultFacility) {
      return facilities.filter(f => f.id === defaultFacility.id);
    }
    return [];
  }, [isAdmin, isFacilityAdmin, defaultFacility, facilities]);

  // Get facility groups with their facilities filtered for the current user
  const getAccessibleFacilityGroups = useCallback(() => {
    const accessibleFacilities = getAccessibleFacilities();
    const accessibleFacilityIds = new Set(accessibleFacilities.map(f => f.id));

    return facilityGroups.map(group => ({
      ...group,
      facilities: (group.facilities || []).filter(facilityId => accessibleFacilityIds.has(facilityId))
    })).filter(group => group.facilities.length > 0);
  }, [facilityGroups, getAccessibleFacilities]);

  // Get ungrouped facilities (facilities not in any group)
  const getUngroupedFacilities = useCallback(() => {
    const accessibleFacilities = getAccessibleFacilities();
    const groupedFacilityIds = new Set();
    
    facilityGroups.forEach(group => {
      (group.facilities || []).forEach(facilityId => groupedFacilityIds.add(facilityId));
    });

    return accessibleFacilities.filter(f => !groupedFacilityIds.has(f.id));
  }, [facilityGroups, getAccessibleFacilities]);

  const loadConfigurations = useCallback(async () => {
    try {
      // Load master configurations - facility assignments are stored in each config
      const configs = await ReportConfigService.getConfigurationsGrouped(true, null, false);
      setConfigurations(configs);
    } catch (error) {
      console.error('Error loading configurations:', error);
      throw error;
    }
  }, []);

  const loadFacilitiesAndGroups = useCallback(async () => {
    try {
      const [facilitiesData, groupsData] = await Promise.all([
        getAllFacilities(),
        getAllFacilityGroups()
      ]);
      setFacilities(facilitiesData);
      setFacilityGroups(groupsData);
      
      // Expand all groups by default
      const expanded = {};
      groupsData.forEach(group => {
        expanded[group.id] = true;
      });
      expanded['ungrouped'] = true;
      setExpandedGroups(expanded);
    } catch (error) {
      console.error('Error loading facilities and groups:', error);
      throw error;
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadConfigurations(),
        loadFacilitiesAndGroups()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  }, [loadConfigurations, loadFacilitiesAndGroups]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleEditReport = (reportType, reportKey) => {
    const config = configurations[reportType]?.[reportKey] || {
      reportType,
      reportKey,
      enabled: true,
      settings: {},
      assignedFacilities: []
    };
    setCurrentReport({ reportType, reportKey });
    setReportConfig(config);
    setSelectedFacilities(config.assignedFacilities || []);
    setEditDialogOpen(true);
  };

  const handleSaveReport = async () => {
    try {
      const configData = {
        ...reportConfig,
        reportType: currentReport.reportType,
        reportKey: currentReport.reportKey,
        isMaster: true, // All configs are now master configs with facility assignments
        facilityId: null,
        assignedFacilities: selectedFacilities
      };

      if (reportConfig.id) {
        await ReportConfigService.updateConfiguration(reportConfig.id, configData, user.uid);
        showSnackbar('Report configuration updated successfully');
      } else {
        await ReportConfigService.createConfiguration(configData, user.uid);
        showSnackbar('Report configuration created successfully');
      }

      setEditDialogOpen(false);
      loadConfigurations();
    } catch (error) {
      console.error('Error saving report configuration:', error);
      showSnackbar('Error saving configuration', 'error');
    }
  };

  const handleDeleteConfig = async (configId) => {
    try {
      await ReportConfigService.deleteConfiguration(configId);
      showSnackbar('Configuration deleted successfully');
      loadConfigurations();
    } catch (error) {
      console.error('Error deleting configuration:', error);
      showSnackbar('Error deleting configuration', 'error');
    }
  };

  const handleToggleReport = async (report, config, checked) => {
    const newConfig = config || { 
      reportType: currentReportType, 
      reportKey: report.key, 
      settings: {},
      assignedFacilities: []
    };
    
    const configData = {
      ...newConfig,
      enabled: checked,
      reportType: currentReportType,
      reportKey: report.key,
      isMaster: true,
      facilityId: null
    };

    try {
      if (config?.id) {
        await ReportConfigService.updateConfiguration(config.id, configData, user.uid);
      } else {
        await ReportConfigService.createConfiguration(configData, user.uid);
      }
      
      setTimeout(() => loadConfigurations(), 500);
    } catch (error) {
      console.error('Error toggling report:', error);
      showSnackbar('Error updating report status', 'error');
    }
  };

  // Facility tree handlers
  const handleToggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const handleToggleFacility = (facilityId) => {
    setSelectedFacilities(prev => {
      if (prev.includes(facilityId)) {
        return prev.filter(id => id !== facilityId);
      } else {
        return [...prev, facilityId];
      }
    });
  };

  const handleToggleGroupSelection = (groupId, groupFacilityIds) => {
    const allSelected = groupFacilityIds.every(id => selectedFacilities.includes(id));
    
    if (allSelected) {
      // Deselect all facilities in this group
      setSelectedFacilities(prev => prev.filter(id => !groupFacilityIds.includes(id)));
    } else {
      // Select all facilities in this group
      setSelectedFacilities(prev => {
        const newSelection = [...prev];
        groupFacilityIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  const handleSelectAll = () => {
    const accessibleFacilities = getAccessibleFacilities();
    const allIds = accessibleFacilities.map(f => f.id);
    
    if (selectedFacilities.length === allIds.length) {
      setSelectedFacilities([]);
    } else {
      setSelectedFacilities(allIds);
    }
  };

  // Get facility name by ID
  const getFacilityName = (facilityId) => {
    const facility = facilities.find(f => f.id === facilityId);
    return facility?.name || facilityId;
  };

  // Render facility tree
  const renderFacilityTree = () => {
    const accessibleGroups = getAccessibleFacilityGroups();
    const ungroupedFacilities = getUngroupedFacilities();
    const accessibleFacilities = getAccessibleFacilities();

    if (accessibleFacilities.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          No facilities available for assignment.
        </Alert>
      );
    }

    return (
      <Box sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 400, overflow: 'auto' }}>
        {/* Select All */}
        <ListItem 
          sx={{ backgroundColor: 'grey.100', borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Checkbox
              checked={selectedFacilities.length === accessibleFacilities.length && accessibleFacilities.length > 0}
              indeterminate={selectedFacilities.length > 0 && selectedFacilities.length < accessibleFacilities.length}
              onChange={handleSelectAll}
            />
          </ListItemIcon>
          <ListItemText 
            primary={<Typography fontWeight="bold">Select All Facilities</Typography>}
            secondary={`${selectedFacilities.length} of ${accessibleFacilities.length} selected`}
          />
        </ListItem>

        <List dense disablePadding>
          {/* Facility Groups */}
          {accessibleGroups.map((group) => {
            const groupFacilityIds = group.facilities || [];
            const selectedInGroup = groupFacilityIds.filter(id => selectedFacilities.includes(id)).length;
            const allInGroupSelected = groupFacilityIds.length > 0 && selectedInGroup === groupFacilityIds.length;
            const someInGroupSelected = selectedInGroup > 0 && selectedInGroup < groupFacilityIds.length;

            return (
              <Box key={group.id}>
                <ListItemButton onClick={() => handleToggleGroup(group.id)}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Checkbox
                      checked={allInGroupSelected}
                      indeterminate={someInGroupSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleToggleGroupSelection(group.id, groupFacilityIds);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </ListItemIcon>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {expandedGroups[group.id] ? <FolderOpenIcon color="primary" /> : <FolderIcon color="primary" />}
                  </ListItemIcon>
                  <ListItemText 
                    primary={group.name}
                    secondary={`${selectedInGroup}/${groupFacilityIds.length} facilities selected`}
                  />
                  {expandedGroups[group.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>

                <Collapse in={expandedGroups[group.id]} timeout="auto" unmountOnExit>
                  <List dense disablePadding sx={{ pl: 4 }}>
                    {groupFacilityIds.map((facilityId) => {
                      const facility = facilities.find(f => f.id === facilityId);
                      if (!facility) return null;

                      return (
                        <ListItem key={facilityId} disablePadding>
                          <ListItemButton onClick={() => handleToggleFacility(facilityId)} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <Checkbox
                                edge="start"
                                checked={selectedFacilities.includes(facilityId)}
                                tabIndex={-1}
                                disableRipple
                              />
                            </ListItemIcon>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <FacilityIcon fontSize="small" color="action" />
                            </ListItemIcon>
                            <ListItemText primary={facility.name} />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          })}

          {/* Ungrouped Facilities */}
          {ungroupedFacilities.length > 0 && (
            <Box>
              <ListItemButton onClick={() => handleToggleGroup('ungrouped')}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Checkbox
                    checked={ungroupedFacilities.every(f => selectedFacilities.includes(f.id))}
                    indeterminate={
                      ungroupedFacilities.some(f => selectedFacilities.includes(f.id)) &&
                      !ungroupedFacilities.every(f => selectedFacilities.includes(f.id))
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleGroupSelection('ungrouped', ungroupedFacilities.map(f => f.id));
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </ListItemIcon>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {expandedGroups['ungrouped'] ? <FolderOpenIcon /> : <FolderIcon />}
                </ListItemIcon>
                <ListItemText 
                  primary="Ungrouped Facilities"
                  secondary={`${ungroupedFacilities.filter(f => selectedFacilities.includes(f.id)).length}/${ungroupedFacilities.length} facilities selected`}
                />
                {expandedGroups['ungrouped'] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>

              <Collapse in={expandedGroups['ungrouped']} timeout="auto" unmountOnExit>
                <List dense disablePadding sx={{ pl: 4 }}>
                  {ungroupedFacilities.map((facility) => (
                    <ListItem key={facility.id} disablePadding>
                      <ListItemButton onClick={() => handleToggleFacility(facility.id)} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Checkbox
                            edge="start"
                            checked={selectedFacilities.includes(facility.id)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <FacilityIcon fontSize="small" color="action" />
                        </ListItemIcon>
                        <ListItemText primary={facility.name} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          )}
        </List>
      </Box>
    );
  };

  const renderReportTable = () => {
    const reports = REPORT_TYPES[currentReportType].reports;

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Report Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Assigned Facilities</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '10%' }} align="center">Enabled</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '10%' }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.map((report) => {
              const config = configurations[currentReportType]?.[report.key];
              const isConfigured = !!config;
              const isEnabled = config?.enabled || false;
              const assignedCount = config?.assignedFacilities?.length || 0;

              return (
                <TableRow 
                  key={report.key}
                  sx={{ 
                    '&:hover': { backgroundColor: 'action.hover' },
                    backgroundColor: isEnabled ? 'rgba(76, 175, 80, 0.08)' : 'inherit'
                  }}
                >
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {report.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {report.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {assignedCount > 0 ? (
                      <Tooltip title={config.assignedFacilities.map(id => getFacilityName(id)).join(', ')}>
                        <Chip 
                          size="small" 
                          label={`${assignedCount} ${assignedCount === 1 ? 'facility' : 'facilities'}`}
                          color="primary"
                          variant="outlined"
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not assigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {isConfigured && (
                      <Chip 
                        size="small" 
                        label={isEnabled ? 'Enabled' : 'Disabled'}
                        color={isEnabled ? 'success' : 'default'}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Switch 
                      checked={isEnabled}
                      onChange={(e) => handleToggleReport(report, config, e.target.checked)}
                      disabled={!canEdit}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={0.5}>
                      <Tooltip title="Configure & Assign Facilities">
                        <span>
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditReport(currentReportType, report.key)}
                            disabled={!canEdit}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {isConfigured && (
                        <Tooltip title="Delete Configuration">
                          <span>
                            <IconButton 
                              size="small" 
                              onClick={() => handleDeleteConfig(config.id)}
                              disabled={!canEdit}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Report Configuration
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Configure reports and assign them to facilities
            </Typography>
          </Box>
        </CardContent>
      </HeaderCard>

      <Paper sx={{ width: '100%', mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {Object.entries(REPORT_TYPES).map(([key, type], index) => (
            <Tab 
              key={key}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  {type.icon}
                  {type.label}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Paper>

      {renderReportTable()}

      {/* Edit Report Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Configure Report: {currentReport && REPORT_TYPES[currentReport.reportType]?.reports.find(r => r.key === currentReport.reportKey)?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControlLabel
              control={
                <Switch 
                  checked={reportConfig.enabled || false}
                  onChange={(e) => setReportConfig({ ...reportConfig, enabled: e.target.checked })}
                />
              }
              label="Enable this report"
              sx={{ mb: 3 }}
            />
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>
              Report Settings
            </Typography>
            
            <TextField
              fullWidth
              label="Report Title"
              value={reportConfig.settings?.title || ''}
              onChange={(e) => setReportConfig({
                ...reportConfig,
                settings: { ...reportConfig.settings, title: e.target.value }
              })}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={reportConfig.settings?.description || ''}
              onChange={(e) => setReportConfig({
                ...reportConfig,
                settings: { ...reportConfig.settings, description: e.target.value }
              })}
              sx={{ mb: 2 }}
            />
            
            <FormControlLabel
              control={
                <Switch 
                  checked={reportConfig.settings?.autoGenerate || false}
                  onChange={(e) => setReportConfig({
                    ...reportConfig,
                    settings: { ...reportConfig.settings, autoGenerate: e.target.checked }
                  })}
                />
              }
              label="Auto-generate report"
              sx={{ mb: 2 }}
            />
            
            {reportConfig.settings?.autoGenerate && (
              <TextField
                fullWidth
                label="Generation Frequency (hours)"
                type="number"
                value={reportConfig.settings?.frequency || 24}
                onChange={(e) => setReportConfig({
                  ...reportConfig,
                  settings: { ...reportConfig.settings, frequency: parseInt(e.target.value) }
                })}
                sx={{ mb: 2 }}
              />
            )}

            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>
              Facility Assignment
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Select which facilities can use this report. {isAdmin ? 'You can see all facilities.' : 'You can only see your assigned facilities.'}
            </Typography>
            
            {renderFacilityTree()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveReport} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportConfiguration;
